/**
 * Translations for Landing Page and UI Components
 * Supports: English (en), Portuguese (pt), Spanish (es)
 */

export const translations = {
  en: {
    // Landing Page - Header
    header: {
      pricing: 'Pricing',
      blog: 'Blog',
      login: 'Login',
      register: 'Register',
      contact: 'Contact'
    },

    // Landing Page - Hero Section
    hero: {
      heading: {
        us: 'Convert US Bank Statements to Accounting-Ready CSV, OFX, QBO',
        ca: 'Convert Canadian Bank Statements to Accounting-Ready CSV, OFX, QBO',
        gb: 'Convert UK Bank Statements to Accounting-Ready CSV, OFX, QBO',
        br: 'Convert Brazilian Bank Statements to Accounting-Ready CSV, OFX, QBO',
        ar: 'Convert Argentine Bank Statements to Accounting-Ready CSV, OFX, QBO',
        ae: 'Convert UAE Bank Statements to Accounting-Ready CSV, OFX, QBO',
        za: 'Convert SA Bank Statements to Accounting-Ready CSV, OFX, QBO',
        default: 'Convert Bank Statements from Banks Worldwide to Accounting-Ready CSV, OFX, QBO'
      },
      subtitle: 'Easily convert PDF bank statements from all major banks into formatted CSV, OFX, QBO files ready for Sage, QuickBooks, Xero, and other accounting software',
      watchVideo: 'Watch How It Works',
      tryFree: 'Try For Free - 2 Credits'
    },

    // Landing Page - Features Section
    features: {
      privacyTitle: 'Privacy First',
      privacyDesc: 'Your PDFs are processed instantly and never stored. Only transaction data is kept as CSV files.',
      banksTitle: 'All Major Banks',
      banksDesc: 'Supports',  // Followed by bank names from locale config
      readyTitle: 'Accounting Ready',
      readyDesc: 'Formatted CSV files ready to upload to accounting software like Sage, QuickBooks, Xero, Pastel, and more'
    },

    // Landing Page - Pricing Section
    pricing: {
      title: 'Choose Your Plan',
      payPerUse: {
        title: 'Pay Per Use',
        perPage: 'per page',
        perTransaction: 'per 30 converted transactions',
        instantConversion: 'Instant conversion',
        registrationRequired: 'Registration required',
        secureProcessing: 'Secure processing',
        button: 'Start Converting'
      },
      creditPacks: {
        title: 'Credit Packs',
        subtitle: 'Save with bulk credits',
        bestValue: 'Best Value',
        credits: 'credits',
        perPageSuffix: 'per page',
        bestValueSuffix: 'Best Value!',
        neverExpire: 'Credits never expire',
        priorityProcessing: 'Priority processing',
        bulkDiscounts: 'Bulk discounts',
        button: 'Buy Credits'
      }
    },

    // Date Format Selector
    dateFormat: {
      label: 'Date Format',
      usFormat: 'US Format (MM/DD/YYYY)',
      intlFormat: 'International (DD/MM/YYYY)',
      isoFormat: 'ISO Format (YYYY-MM-DD)',
      preview: 'Dates will appear as:'
    },

    // Language Switcher
    language: {
      english: 'English',
      portuguese: 'Português',
      spanish: 'Español'
    },

    // Dashboard
    dashboard: {
      title: 'PDF Statement Converter',
      trialMode: 'Trial Mode - Try our service for free!',
      trialDescription: 'Convert your bank statements to CSV, OFX, or QBO format. Get 2 free credits to test our service.',
      creditsAvailable: 'Credits Available',
      freeTrialCredits: 'Free Trial Credits',
      registerFreeCredits: 'Register to Get 10 Free Credits',
      buyCredits: 'Buy Credits',
      processing: 'Processing',
      logout: 'Logout',
      signingOut: 'Signing out',
      uploadNew: 'Upload New Statement',
      recentConversions: 'Recent Conversions',
      privacyNote: 'CSV files are automatically deleted after 30 days for privacy protection',
      noConversions: 'No conversions yet',
      uploadFirst: 'Upload your first bank statement to get started',
      fileName: 'File Name',
      date: 'Date',
      status: 'Status',
      actions: 'Actions',
      completed: 'Completed',
      failed: 'Failed',
      download: 'Download',
      view: 'View',
      delete: 'Delete',
      upgradeAccount: 'Upgrade Account'
    },

    // Footer
    footer: {
      description: {
        us: 'Convert US bank statements to accounting-ready CSV format',
        ca: 'Convert Canadian bank statements to accounting-ready CSV format',
        gb: 'Convert UK bank statements to accounting-ready CSV format',
        br: 'Convert Brazilian bank statements to accounting-ready CSV format',
        ar: 'Convert Argentine bank statements to accounting-ready CSV format',
        ae: 'Convert UAE bank statements to accounting-ready CSV format',
        za: 'Convert South African bank statements to accounting-ready CSV format',
        default: 'Convert bank statements to accounting-ready CSV format'
      }
    }
  },

  pt: {
    // Landing Page - Header (Portuguese - Brazil)
    header: {
      pricing: 'Preços',
      blog: 'Blog',
      login: 'Entrar',
      register: 'Cadastrar',
      contact: 'Contato'
    },

    // Landing Page - Hero Section
    hero: {
      heading: {
        br: 'Converta Extratos Bancários Brasileiros para CSV, OFX, QBO Prontos para Contabilidade',
        default: 'Converta Extratos Bancários para CSV, OFX, QBO Prontos para Contabilidade'
      },
      subtitle: 'Converta facilmente extratos bancários em PDF de todos os principais bancos em arquivos CSV, OFX, QBO formatados e prontos para Sage, QuickBooks, Xero e outros softwares de contabilidade',
      watchVideo: 'Veja Como Funciona',
      tryFree: 'Experimente Grátis - 2 Créditos'
    },

    // Landing Page - Features Section
    features: {
      privacyTitle: 'Privacidade em Primeiro Lugar',
      privacyDesc: 'Seus PDFs são processados instantaneamente e nunca armazenados. Apenas os dados das transações são mantidos como arquivos CSV.',
      banksTitle: 'Todos os Principais Bancos',
      banksDesc: 'Suporta',  // Followed by bank names from locale config
      readyTitle: 'Pronto para Contabilidade',
      readyDesc: 'Arquivos CSV formatados prontos para upload em softwares de contabilidade como Sage, QuickBooks, Xero, Pastel e outros'
    },

    // Landing Page - Pricing Section
    pricing: {
      title: 'Escolha Seu Plano',
      payPerUse: {
        title: 'Pague Por Uso',
        perPage: 'por página',
        perTransaction: 'por 30 transações convertidas',
        instantConversion: 'Conversão instantânea',
        registrationRequired: 'Cadastro necessário',
        secureProcessing: 'Processamento seguro',
        button: 'Começar a Converter'
      },
      creditPacks: {
        title: 'Pacotes de Créditos',
        subtitle: 'Economize com créditos em massa',
        bestValue: 'Melhor Valor',
        credits: 'créditos',
        perPageSuffix: 'por página',
        bestValueSuffix: 'Melhor Valor!',
        neverExpire: 'Créditos nunca expiram',
        priorityProcessing: 'Processamento prioritário',
        bulkDiscounts: 'Descontos em massa',
        button: 'Comprar Créditos'
      }
    },

    // Date Format Selector
    dateFormat: {
      label: 'Formato de Data',
      usFormat: 'Formato EUA (MM/DD/YYYY)',
      intlFormat: 'Internacional (DD/MM/YYYY)',
      isoFormat: 'Formato ISO (YYYY-MM-DD)',
      preview: 'As datas aparecerão como:'
    },

    // Language Switcher
    language: {
      english: 'English',
      portuguese: 'Português',
      spanish: 'Español'
    },

    // Dashboard
    dashboard: {
      title: 'Conversor de Extratos PDF',
      trialMode: 'Modo de Teste - Experimente nosso serviço gratuitamente!',
      trialDescription: 'Converta seus extratos bancários para formato CSV, OFX ou QBO. Ganhe 2 créditos gratuitos para testar nosso serviço.',
      creditsAvailable: 'Créditos Disponíveis',
      freeTrialCredits: 'Créditos de Teste Gratuitos',
      registerFreeCredits: 'Registrar para Ganhar 10 Créditos Gratuitos',
      buyCredits: 'Comprar Créditos',
      processing: 'Processando',
      logout: 'Sair',
      signingOut: 'Saindo',
      uploadNew: 'Enviar Novo Extrato',
      recentConversions: 'Conversões Recentes',
      privacyNote: 'Arquivos CSV são automaticamente excluídos após 30 dias para proteção de privacidade',
      noConversions: 'Nenhuma conversão ainda',
      uploadFirst: 'Envie seu primeiro extrato bancário para começar',
      fileName: 'Nome do Arquivo',
      date: 'Data',
      status: 'Status',
      actions: 'Ações',
      completed: 'Concluído',
      failed: 'Falhou',
      download: 'Baixar',
      view: 'Ver',
      delete: 'Excluir',
      upgradeAccount: 'Atualizar Conta'
    },

    // Footer
    footer: {
      description: {
        us: 'Converta extratos bancários dos EUA para formato CSV pronto para contabilidade',
        ca: 'Converta extratos bancários canadenses para formato CSV pronto para contabilidade',
        gb: 'Converta extratos bancários do Reino Unido para formato CSV pronto para contabilidade',
        br: 'Converta extratos bancários brasileiros para formato CSV pronto para contabilidade',
        ar: 'Converta extratos bancários argentinos para formato CSV pronto para contabilidade',
        ae: 'Converta extratos bancários dos Emirados Árabes para formato CSV pronto para contabilidade',
        za: 'Converta extratos bancários sul-africanos para formato CSV pronto para contabilidade',
        default: 'Converta extratos bancários para formato CSV pronto para contabilidade'
      }
    }
  },

  es: {
    // Landing Page - Header (Spanish - Argentina)
    header: {
      pricing: 'Precios',
      blog: 'Blog',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      contact: 'Contacto'
    },

    // Landing Page - Hero Section
    hero: {
      heading: {
        ar: 'Convierte Estados de Cuenta Bancarios Argentinos a CSV, OFX, QBO Listos para Contabilidad',
        default: 'Convierte Estados de Cuenta Bancarios a CSV, OFX, QBO Listos para Contabilidad'
      },
      subtitle: 'Convierte fácilmente estados de cuenta bancarios en PDF de todos los bancos principales en archivos CSV, OFX, QBO formateados y listos para Sage, QuickBooks, Xero y otros softwares de contabilidad',
      watchVideo: 'Ver Cómo Funciona',
      tryFree: 'Probar Gratis - 2 Créditos'
    },

    // Landing Page - Features Section
    features: {
      privacyTitle: 'Privacidad Primero',
      privacyDesc: 'Tus PDFs se procesan instantáneamente y nunca se almacenan. Solo los datos de transacciones se guardan como archivos CSV.',
      banksTitle: 'Todos los Bancos Principales',
      banksDesc: 'Soporta',  // Followed by bank names from locale config
      readyTitle: 'Listo para Contabilidad',
      readyDesc: 'Archivos CSV formateados listos para subir a software de contabilidad como Sage, QuickBooks, Xero, Pastel y más'
    },

    // Landing Page - Pricing Section
    pricing: {
      title: 'Elige Tu Plan',
      payPerUse: {
        title: 'Pago Por Uso',
        perPage: 'por página',
        perTransaction: 'por 30 transacciones convertidas',
        instantConversion: 'Conversión instantánea',
        registrationRequired: 'Registro requerido',
        secureProcessing: 'Procesamiento seguro',
        button: 'Comenzar a Convertir'
      },
      creditPacks: {
        title: 'Paquetes de Créditos',
        subtitle: 'Ahorra con créditos en masa',
        bestValue: 'Mejor Valor',
        credits: 'créditos',
        perPageSuffix: 'por página',
        bestValueSuffix: '¡Mejor Valor!',
        neverExpire: 'Los créditos nunca expiran',
        priorityProcessing: 'Procesamiento prioritario',
        bulkDiscounts: 'Descuentos por volumen',
        button: 'Comprar Créditos'
      }
    },

    // Date Format Selector
    dateFormat: {
      label: 'Formato de Fecha',
      usFormat: 'Formato EE.UU. (MM/DD/YYYY)',
      intlFormat: 'Internacional (DD/MM/YYYY)',
      isoFormat: 'Formato ISO (YYYY-MM-DD)',
      preview: 'Las fechas aparecerán como:'
    },

    // Language Switcher
    language: {
      english: 'English',
      portuguese: 'Português',
      spanish: 'Español'
    },

    // Dashboard
    dashboard: {
      title: 'Conversor de Estados de Cuenta PDF',
      trialMode: 'Modo de Prueba - ¡Prueba nuestro servicio gratis!',
      trialDescription: 'Convierte tus estados de cuenta bancarios a formato CSV, OFX o QBO. Obtén 2 créditos gratuitos para probar nuestro servicio.',
      creditsAvailable: 'Créditos Disponibles',
      freeTrialCredits: 'Créditos de Prueba Gratuitos',
      registerFreeCredits: 'Registrarse para Obtener 10 Créditos Gratis',
      buyCredits: 'Comprar Créditos',
      processing: 'Procesando',
      logout: 'Cerrar Sesión',
      signingOut: 'Cerrando Sesión',
      uploadNew: 'Subir Nuevo Estado de Cuenta',
      recentConversions: 'Conversiones Recientes',
      privacyNote: 'Los archivos CSV se eliminan automáticamente después de 30 días para protección de privacidad',
      noConversions: 'Aún no hay conversiones',
      uploadFirst: 'Sube tu primer estado de cuenta bancario para comenzar',
      fileName: 'Nombre del Archivo',
      date: 'Fecha',
      status: 'Estado',
      actions: 'Acciones',
      completed: 'Completado',
      failed: 'Fallido',
      download: 'Descargar',
      view: 'Ver',
      delete: 'Eliminar',
      upgradeAccount: 'Actualizar Cuenta'
    },

    // Footer
    footer: {
      description: {
        us: 'Convierte estados de cuenta bancarios de EE.UU. a formato CSV listo para contabilidad',
        ca: 'Convierte estados de cuenta bancarios canadienses a formato CSV listo para contabilidad',
        gb: 'Convierte estados de cuenta bancarios del Reino Unido a formato CSV listo para contabilidad',
        br: 'Convierte estados de cuenta bancarios brasileños a formato CSV listo para contabilidad',
        ar: 'Convierte estados de cuenta bancarios argentinos a formato CSV listo para contabilidad',
        ae: 'Convierte estados de cuenta bancarios de los Emiratos Árabes a formato CSV listo para contabilidad',
        za: 'Convierte estados de cuenta bancarios sudafricanos a formato CSV listo para contabilidad',
        default: 'Convierte estados de cuenta bancarios a formato CSV listo para contabilidad'
      }
    }
  }
};

/**
 * Get translation text
 * @param {string} language - Language code ('en', 'pt', 'es')
 * @param {string} key - Dot-notation key (e.g., 'hero.heading.us')
 * @returns {string} Translated text or key if not found
 */
export function t(language, key) {
  const keys = key.split('.');
  let value = translations[language];

  if (!value) {
    console.warn(`Translation language not found: ${language}`);
    return key;
  }

  for (const k of keys) {
    value = value[k];
    if (value === undefined) {
      console.warn(`Translation key not found: ${key} in ${language}`);
      return key;
    }
  }

  return value;
}

/**
 * Get heading translation based on country
 * @param {string} language - Language code
 * @param {string} countryCode - Country code
 * @returns {string} Localized heading
 */
export function getHeading(language, countryCode) {
  const headingKey = `hero.heading.${countryCode.toLowerCase()}`;
  const heading = t(language, headingKey);

  // If country-specific heading doesn't exist, use default
  if (heading === headingKey) {
    return t(language, 'hero.heading.default');
  }

  return heading;
}

/**
 * Get footer description translation based on country
 * @param {string} language - Language code
 * @param {string} countryCode - Country code
 * @returns {string} Localized footer description
 */
export function getFooterDescription(language, countryCode) {
  const descKey = `footer.description.${countryCode.toLowerCase()}`;
  const description = t(language, descKey);

  // If country-specific description doesn't exist, use default
  if (description === descKey) {
    return t(language, 'footer.description.default');
  }

  return description;
}

export default translations;
